import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Eye, EyeOff, Loader2, Send, ChevronDown, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import { format } from 'date-fns'
import {
  useProfiles,
  useQueues,
  useCreatePost,
  useUpdatePost
} from '@/features/social/hooks'
import type { SocialPost } from '@/features/social/types'
import { POST_STATUS } from '@/constants/socialPostStatus'
import InstagramPreview from '@/features/social/components/preview/InstagramPreview'
import { CreatePostCommandBar } from '@/features/social/components/command-bar/CreatePostCommandBar'
import { PostCreateStepper, type PostCreateStep } from '@/features/social/components/post-create/PostCreateStepper'
import {
  validateSocialPost,
  getFieldError,
  hasFieldError,
  type ValidationError,
  type ValidationResult
} from '@/lib/validation/socialPostValidation'
import { cn } from '@/lib/utils'
import { isFeatureEnabled } from '@/config/gmb-settings'
import type { CaptionAgentContext } from '@/features/social/types/captionAgent'

// Imported sub-components
import { PostProfileSelector } from './PostProfileSelector'
import { PostContentEditor } from './PostContentEditor'
import { PostMediaUploader } from './PostMediaUploader'
import { PostTagsEditor } from './PostTagsEditor'
import { PostScheduler } from './PostScheduler'
import { PostPreviewSection } from './PostPreviewSection'

type PostFormState = {
  caption: string
  content: string
  image_urls: string[]
  profile_id: string
  queue_id: string
  scheduled_time: string | null
  hashtags: string[]
  tags: string[]
  status: SocialPost['status']
}

const STEP_SEQUENCE = ['profile', 'content', 'media', 'tags', 'publish'] as const
type StepKey = typeof STEP_SEQUENCE[number]
const isStepKey = (value: string): value is StepKey =>
  (STEP_SEQUENCE as readonly string[]).includes(value)

function StepHeading({ stepNumber, title }: { stepNumber: number; title: string }) {
  return (
    <CardTitle className="text-lg font-semibold flex items-center gap-2">
      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
        <span className="text-primary text-sm font-bold">{stepNumber}</span>
      </div>
      {title}
    </CardTitle>
  )
}

interface PostCreateFormProps {
  initialData?: Partial<SocialPost>
  postContext?: CaptionAgentContext
  onClose: () => void
  onSuccess?: () => void
  /** Called when a gallery is selected, to update postContext for Caption Agent */
  onGallerySelect?: (galleryId: number) => void
}

export default function PostCreateForm({ initialData, postContext, onClose, onSuccess, onGallerySelect }: PostCreateFormProps) {
  const isFullPage = !initialData?.id // Detect if this is a new post (full page) vs edit (dialog)
  const { data: profiles = [] } = useProfiles()
  const defaultProfileId = initialData?.profile_id ?? ''
  const [selectedProfileId, setSelectedProfileId] = useState(defaultProfileId)
  const { data: queues = [] } = useQueues(selectedProfileId)
  const { error: toastError } = useToast()

  const createPost = useCreatePost()
  const updatePost = useUpdatePost()

  const [formData, setFormData] = useState<PostFormState>({
    caption: initialData?.caption ?? '',
    content: initialData?.content ?? '',
    image_urls: initialData?.image_urls ?? [],
    profile_id: defaultProfileId,
    queue_id: initialData?.queue_id ?? '',
    scheduled_time: initialData?.scheduled_time ?? null,
    hashtags: initialData?.hashtags ?? [],
    tags: initialData?.tags ?? [],
    status: initialData?.status ?? POST_STATUS.DRAFT
  })
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>(() =>
    defaultProfileId ? [defaultProfileId] : []
  )

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPreview, setShowPreview] = useState(true) // Show preview by default on desktop
  const [isMobile, setIsMobile] = useState(false)
  const enhancedUI = isFeatureEnabled('ui.createPostEnhancedUI')
  const [expandedStep, setExpandedStep] = useState<StepKey>('profile')
  const [hasManuallySelectedStep, setHasManuallySelectedStep] = useState(false)
  const multiProfileEnabled = enhancedUI && isFeatureEnabled('ui.multiProfilePosting') && isFullPage

  const formRef = useRef<HTMLFormElement>(null)
  const profileSectionRef = useRef<HTMLDivElement>(null)
  const contentSectionRef = useRef<HTMLDivElement>(null)
  const mediaSectionRef = useRef<HTMLDivElement>(null)
  const tagsSectionRef = useRef<HTMLDivElement>(null)
  const publishSectionRef = useRef<HTMLDivElement>(null)

  const sectionRefs: Record<StepKey, React.RefObject<HTMLDivElement>> = {
    profile: profileSectionRef,
    content: contentSectionRef,
    media: mediaSectionRef,
    tags: tagsSectionRef,
    publish: publishSectionRef
  }

  const selectedProfilesList = useMemo(
    () => profiles.filter(profile => selectedProfileIds.includes(profile.id)),
    [profiles, selectedProfileIds]
  )

  useEffect(() => {
    const nextPrimary = selectedProfileIds[0] || ''
    setSelectedProfileId(prev => (prev === nextPrimary ? prev : nextPrimary))
    setFormData(prev => (prev.profile_id === nextPrimary ? prev : { ...prev, profile_id: nextPrimary }))
  }, [selectedProfileIds])

  // Get the current platform for platform-specific validation
  const currentProfile = selectedProfilesList[0] || null
  const currentPlatform = currentProfile?.platform
  const selectedQueue = queues.find(queue => queue.id === formData.queue_id)
  const selectedProfileCount = selectedProfilesList.length
  const scheduleSummary = formData.scheduled_time
    ? `Scheduled for ${format(new Date(formData.scheduled_time), 'PP • p')}${selectedProfileCount > 1 ? ' · All profiles' : ''}`
    : selectedProfileCount > 1
      ? 'Publishes immediately for all profiles'
      : 'Publishes immediately after publish'
  const queueSummary = selectedQueue
    ? selectedProfileCount > 1
      ? `${selectedQueue.name} · ${selectedProfileCount} profiles`
      : `${selectedQueue.name} queue`
    : selectedProfileCount > 1
      ? 'Shared queue not set'
      : undefined
  const validationActive = touchedFields.size > 0 && validationErrors.length > 0
  const validationMessage = validationActive
    ? `${validationErrors.length} issue${validationErrors.length === 1 ? '' : 's'} remaining`
    : undefined
  const commandBarTitle = initialData?.id ? 'Update Social Post' : 'Create Social Media Post'
  const currentStatus = formData.status ?? POST_STATUS.DRAFT

  const postCreateSteps = useMemo<PostCreateStep[]>(() => {
    const meta = [
      {
        id: 'profile',
        title: 'Select Profile',
        description: selectedProfileCount > 1
          ? `${selectedProfileCount} profiles selected`
          : currentProfile ? currentProfile.name : 'Choose destination',
        isComplete: selectedProfileCount > 0,
        hasError: hasFieldError(validationErrors, 'profile_id')
      },
      {
        id: 'content',
        title: 'Post Content',
        description: `${formData.caption.length} characters`,
        isComplete: formData.caption.trim().length > 0,
        hasError: hasFieldError(validationErrors, 'caption')
      },
      {
        id: 'media',
        title: 'Media',
        description: formData.image_urls.length > 0 ? `${formData.image_urls.length} asset${formData.image_urls.length === 1 ? '' : 's'}` : 'Add visuals',
        isComplete: formData.image_urls.length > 0,
        hasError: hasFieldError(validationErrors, 'image_urls')
      },
      {
        id: 'tags',
        title: 'Tags & Hashtags',
        description: `${formData.hashtags.length} hashtag${formData.hashtags.length === 1 ? '' : 's'}`,
        isComplete: formData.hashtags.length > 0,
        hasError: hasFieldError(validationErrors, 'hashtags')
      },
      {
        id: 'publish',
        title: 'Publishing Options',
        description: formData.scheduled_time ? scheduleSummary : 'Publish immediately',
        isComplete: Boolean(formData.queue_id || formData.scheduled_time),
        hasError: hasFieldError(validationErrors, 'scheduled_time')
      }
    ]

    const firstErrorIndex = meta.findIndex(step => step.hasError)
    const firstIncompleteIndex = meta.findIndex(step => !step.isComplete)
    const activeIndex = firstErrorIndex >= 0
      ? firstErrorIndex
      : firstIncompleteIndex >= 0
        ? firstIncompleteIndex
        : meta.length - 1

    return meta.map((step, index) => {
      let status: PostCreateStep['status'] = 'upcoming'
      if (step.hasError) status = 'error'
      else if (step.isComplete) status = 'complete'
      else if (index === activeIndex) status = 'current'

      return {
        id: step.id,
        number: index + 1,
        title: step.title,
        description: step.description,
        status
      }
    })
  }, [
    currentProfile,
    formData.caption,
    formData.hashtags.length,
    formData.image_urls.length,
    formData.queue_id,
    formData.scheduled_time,
    scheduleSummary,
    validationErrors,
    selectedProfileCount
  ])

  const activeStepId = postCreateSteps.find(step => step.status === 'current')?.id
    ?? postCreateSteps.find(step => step.status === 'error')?.id
    ?? postCreateSteps[postCreateSteps.length - 1]?.id

  useEffect(() => {
    if (!enhancedUI || !activeStepId || !isStepKey(activeStepId)) return
    if (!hasManuallySelectedStep && expandedStep !== activeStepId) {
      setExpandedStep(activeStepId)
    }
  }, [activeStepId, enhancedUI, expandedStep, hasManuallySelectedStep])

  const scrollToStep = (stepId: StepKey) => {
    const ref = sectionRefs[stepId]
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const triggerSubmit = () => formRef.current?.requestSubmit()

  const handleCommandPublish = () => {
    triggerSubmit()
  }

  const handleCommandSaveDraft = () => {
    setFormData(prev => ({ ...prev, status: POST_STATUS.DRAFT }))
    triggerSubmit()
  }

  const handleStepperSelect = (stepId: string) => {
    if (!isStepKey(stepId)) return
    if (enhancedUI) {
      setExpandedStep(stepId)
      setHasManuallySelectedStep(true)
    }
    scrollToStep(stepId)
  }

  const renderSection = (stepKey: StepKey, stepNumber: number, title: string, content: React.ReactNode) => {
    const ref = sectionRefs[stepKey]

    if (!enhancedUI) {
      return (
        <div ref={ref}>
          <Card className="border-border/40 shadow-sm">
            <CardHeader className="pb-4">
              <StepHeading stepNumber={stepNumber} title={title} />
            </CardHeader>
            <CardContent>
              {content}
            </CardContent>
          </Card>
        </div>
      )
    }

    const isOpen = expandedStep === stepKey

    return (
      <div ref={ref}>
        <Collapsible
          open={isOpen}
          onOpenChange={(open) => {
            if (open) {
              setExpandedStep(stepKey)
              setHasManuallySelectedStep(true)
            }
          }}
        >
          <Card className="border-border/40 shadow-sm">
            <CollapsibleTrigger asChild>
              <CardHeader className="flex items-center justify-between pb-4 cursor-pointer">
                <StepHeading stepNumber={stepNumber} title={title} />
                <ChevronDown className={cn(
                  'h-5 w-5 text-muted-foreground transition-transform',
                  isOpen && 'rotate-180'
                )} />
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {content}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    )
  }

  useEffect(() => {
    if (!initialData) return

    setFormData(prev => ({
      ...prev,
      caption: initialData.caption ?? '',
      content: initialData.content ?? '',
      image_urls: initialData.image_urls ?? [],
      profile_id: initialData.profile_id ?? '',
      queue_id: initialData.queue_id ?? '',
      scheduled_time: initialData.scheduled_time ?? null,
      hashtags: initialData.hashtags ?? [],
      tags: initialData.tags ?? [],
      status: initialData.status ?? prev.status ?? POST_STATUS.DRAFT
    }))

    setSelectedProfileId(initialData.profile_id ?? '')
    setSelectedProfileIds(initialData.profile_id ? [initialData.profile_id] : [])
  }, [initialData])

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
      // Hide preview on mobile by default
      if (window.innerWidth < 768) {
        setShowPreview(false)
      }
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const validateForm = useCallback((): ValidationResult => {
    const result = validateSocialPost(formData, currentPlatform)
    setValidationErrors(result.errors)
    return result
  }, [formData, currentPlatform])

  // Validate form on data change
  useEffect(() => {
    if (touchedFields.size > 0) {
      validateForm()
    }
  }, [formData, currentPlatform, touchedFields.size, validateForm])

  const markFieldAsTouched = (fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName))
  }

  const getErrorMessage = (fieldName: string): string | null => {
    return touchedFields.has(fieldName) ? getFieldError(validationErrors, fieldName) : null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Prevent double submission
    if (isSubmitting) return

    // Touch all fields to show errors
    setTouchedFields(new Set(['caption', 'profile_id', 'scheduled_time', 'hashtags', 'image_urls']))

    // Validate form
    const validation = validateForm()
    if (!validation.isValid) {
      toastError('Validation Error', 'Please fix the errors before submitting')

      // Focus on first error field
      const firstError = validation.errors[0]
      if (firstError) {
        const fieldElement = document.querySelector(`[name="${firstError.field}"]`)
        if (fieldElement instanceof HTMLElement) {
          fieldElement.focus()
        }
      }
      return
    }

    setIsSubmitting(true)

    const targetProfileIds = multiProfileEnabled
      ? selectedProfileIds
      : (formData.profile_id ? [formData.profile_id] : [])

    if (targetProfileIds.length === 0) {
      toastError('Profile Required', 'Please select at least one profile.')
      setIsSubmitting(false)
      return
    }

    const postData = {
      ...formData,
      profile_id: targetProfileIds[0],
      status: formData.scheduled_time ? POST_STATUS.SCHEDULED : POST_STATUS.DRAFT
    }

    try {
      let result
      if (initialData?.id) {
        result = await updatePost.mutateAsync({ id: initialData.id, ...postData })
        console.log('Update result:', result)
      } else if (multiProfileEnabled && targetProfileIds.length > 1) {
        for (const profileId of targetProfileIds) {
          result = await createPost.mutateAsync({ ...postData, profile_id: profileId })
        }
        console.log('Create result:', result)
      } else {
        result = await createPost.mutateAsync({ ...postData, profile_id: targetProfileIds[0] })
        console.log('Create result:', result)
      }
      onSuccess?.()
      if (!isFullPage) {
        onClose()
      }
    } catch (error) {
      console.error('Failed to save post:', error)
      // Error toast is handled by the mutation hook
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleProfileSelectionChange = (ids: string[]) => {
    setSelectedProfileIds(ids)
    markFieldAsTouched('profile_id')
  }

  return (
    <div className="relative space-y-6">
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium">
              {initialData?.id ? 'Updating post...' : 'Creating post...'}
            </p>
          </div>
        </div>
      )}

      {enhancedUI && (
        <CreatePostCommandBar
          title={commandBarTitle}
          profileName={currentProfile?.name}
          profileAvatar={currentProfile?.profile_picture_url}
          platform={currentPlatform}
          profiles={selectedProfilesList.map(profile => ({
            id: profile.id,
            name: profile.name,
            platform: profile.platform,
            avatar: profile.profile_picture_url
          }))}
          queueLabel={queueSummary}
          scheduleLabel={scheduleSummary}
          status={currentStatus}
          hasValidationErrors={validationActive}
          validationMessage={validationMessage}
          isSubmitting={isSubmitting}
          disablePublish={validationActive}
          showSaveDraft={isFullPage}
          onBack={isFullPage ? onClose : undefined}
          onCancel={onClose}
          onSaveDraft={isFullPage ? handleCommandSaveDraft : undefined}
          onPublish={handleCommandPublish}
        />
      )}

      {!enhancedUI && isFullPage && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Create Social Media Post</h1>
          <p className="text-muted-foreground mt-2">
            Compose and schedule your post across multiple social media platforms
          </p>
        </div>
      )}

      {/* Mobile Preview Toggle */}
      {!enhancedUI && isMobile && currentPlatform === 'INSTAGRAM' && (
        <div className="mb-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className="w-full"
          >
            {showPreview ? (
              <><EyeOff className="h-4 w-4 mr-2" /> Hide Preview</>
            ) : (
              <><Eye className="h-4 w-4 mr-2" /> Show Preview</>
            )}
          </Button>
        </div>
      )}

      {/* Mobile Preview */}
      {!enhancedUI && isMobile && showPreview && currentPlatform === 'INSTAGRAM' && (
        <div className="mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Instagram Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <InstagramPreview
                caption={formData.caption}
                images={formData.image_urls}
                hashtags={formData.hashtags}
                accountName={currentProfile?.name || 'fixmybrick'}
                accountAvatar={currentProfile?.profile_picture_url || undefined}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* Left Column: Form */}
        <div className="lg:col-span-2 space-y-6">
          <form ref={formRef} onSubmit={(e) => { void handleSubmit(e) }} className="space-y-6">
            {enhancedUI && postCreateSteps.length > 0 && (
              <PostCreateStepper
                steps={postCreateSteps}
                activeStepId={activeStepId}
                onStepSelect={handleStepperSelect}
              />
            )}

            {/* Profile Selection */}
            {renderSection('profile', 1, 'Select Profile', (
              <PostProfileSelector
                profiles={profiles}
                selectedProfileIds={selectedProfileIds}
                onSelectionChange={handleProfileSelectionChange}
                multiProfileEnabled={multiProfileEnabled || false}
                error={getErrorMessage('profile_id')}
                isSubmitting={isSubmitting}
              />
            ))}

            {/* Caption */}
            {renderSection('content', 2, 'Post Content', (
              <PostContentEditor
                caption={formData.caption}
                onCaptionChange={(value) => {
                  setFormData({ ...formData, caption: value })
                }}
                onBlur={() => markFieldAsTouched('caption')}
                platform={currentPlatform}
                error={getErrorMessage('caption')}
                isSubmitting={isSubmitting}
                postContext={postContext}
                profileId={formData.profile_id}
                imageUrls={formData.image_urls}
                hashtags={formData.hashtags}
              />
            ))}

            {/* Images */}
            {renderSection('media', 3, 'Media', (
              <PostMediaUploader
                imageUrls={formData.image_urls}
                onImagesChange={(urls) => {
                  setFormData({ ...formData, image_urls: urls })
                  markFieldAsTouched('image_urls')
                }}
                platform={currentPlatform}
                error={getErrorMessage('image_urls')}
                isSubmitting={isSubmitting}
                onGallerySelect={onGallerySelect}
              />
            ))}

            {/* Tags & Hashtags */}
            {renderSection('tags', 4, 'Tags & Hashtags', (
              <PostTagsEditor
                hashtags={formData.hashtags}
                onHashtagsChange={(tags) => {
                  setFormData({ ...formData, hashtags: tags })
                  markFieldAsTouched('hashtags')
                }}
                platform={currentPlatform}
                error={getErrorMessage('hashtags')}
                isSubmitting={isSubmitting}
              />
            ))}

            {/* Scheduling */}
            {renderSection('publish', 5, 'Publishing Options', (
              <PostScheduler
                scheduledTime={formData.scheduled_time}
                onScheduledTimeChange={(time) => {
                  setFormData({ ...formData, scheduled_time: time })
                  markFieldAsTouched('scheduled_time')
                }}
                queueId={formData.queue_id}
                onQueueIdChange={(id) => setFormData({ ...formData, queue_id: id || '' })}
                queues={queues}
                error={getErrorMessage('scheduled_time')}
                isSubmitting={isSubmitting}
              />
            ))}

            {/* Validation Summary */}
            {touchedFields.size > 0 && validationErrors.length > 0 && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 animate-in fade-in slide-in-from-top-2">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-semibold text-destructive">Please fix the following errors:</p>
                    <ul className="text-sm text-destructive space-y-1 list-disc pl-4">
                      {validationErrors.map((error, index) => (
                        <li key={index}>{error.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {!enhancedUI && (
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="text-muted-foreground"
                >
                  Cancel
                </Button>

                {isFullPage && (
                  <Button
                    type="submit"
                    variant="outline"
                    disabled={isSubmitting || validationErrors.length > 0}
                    onClick={() => setFormData(prev => ({ ...prev, status: 'DRAFT' }))}
                  >
                    Save as Draft
                  </Button>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting || (touchedFields.size > 0 && validationErrors.length > 0)}
                  className="min-w-36"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {initialData?.id ? 'Updating...' : 'Creating...'}
                    </>
                  ) : initialData?.id ? (
                    'Update Post'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Create Post
                    </>
                  )}
                </Button>
              </div>
            )}
          </form>
        </div>

        {/* Right Column: Preview */}
        <div className="hidden lg:block space-y-6">
          <PostPreviewSection
            enhancedUI={enhancedUI}
            platform={currentPlatform}
            postData={formData as Partial<SocialPost>}
            currentProfile={currentProfile}
            queueName={queueSummary}
            scheduleSummary={scheduleSummary}
            status={currentStatus}
            validationActive={validationActive}
            validationMessage={validationMessage}
          />
        </div>
      </div>
    </div>
  )
}
